<template v-cloak>
  <p v-if="loading" class="vrgz-incident-comments-loading-message">
    <Spinner />
    {{ loadingMessage }}
  </p>
  <Component
    class="vrgz-incident-comments"
    ref="incidentCommentsRef"
    :class="appClasses"
    reversed
    :is="elementType"
    :aria-label="ariaLabel"
  >
    <Comment
        v-if="sortedComments.length > 0"
        v-for="(comment, index) in sortedComments"
        :key="comment.id"
        :comment="comment"
        :edit="edit"
        :aria-current="0 ===index ? 'step' : null"
    />
    <Alert v-else-if="!loading" type="warning" :message="emptyMessage" />
  </Component>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted, computed, provide } from 'vue';
import Comment from './components/Comment.vue';
import Spinner from './components/Spinner.vue';
import Alert from "./components/Alert.vue";

export default defineComponent( {
  name: 'IncidentStatus',
  components: {
    Alert,
    Comment,
    Spinner
  },
  props: {
    existingComments: {
      type: Array,
      default: () => [],
    },
    emptyMessage: {
      type: String,
      default: 'No comments yet...',
    },
    loadingMessage: {
      type: String,
      default: 'Loading comments...',
    },
    incidentId: {
      type: Number,
      required: true,
    },
    updateInterval: {
      type: Number,
      default: 60,
    }
  },
  setup( props ) {
    const incidentCommentsRef = ref( null );
    const loading = ref( true );
    const comments = ref( [ ...props.existingComments ] );
    const commentLength = ref( comments.value.length );
    const edit = ref( !! window.vrgz_variables.comment_edit ? window.vrgz_variables.comment_edit : false );
    let intervalId = null;
    const elementType = ref( 'ol' );

    const { labels } = window.vrgz_variables.timeline;
    const ariaLabel = computed( () => 'ol' === elementType ? labels.aria : null );
    provide( 'labels', labels );

    // Merging `existingComments` and `comments` and removing duplicates based on `id`
    const sortedComments = computed( () => {
      const uniqueCommentsMap = new Map();
      [ ...props.existingComments, ...comments.value ]?.forEach( comment => uniqueCommentsMap.set( comment.id, comment ) );

      // Convert the Map back to an array and sort by datetime in descending order
      return Array.from( uniqueCommentsMap.values() ).sort( ( a, b ) => b.datetime - a.datetime );
    } );

    const appClasses = computed( () => ( {
      'vrgz-incident-comments--loading': !! loading.value,
      'vrgz-incident-comments--empty': 0 === sortedComments.value.length,
    } ) );

    const fetchComments = async () => {
      try {
        loading.value = true;
        const { url, route } = window.vrgz_variables.rest;

        await fetch( `${ url }${route}/incident/${ props.incidentId }` )
            .then( response => {
              if ( ! response.ok ) {
                throw new Error( 'Network response was not ok' );
              }
              return response.json();
            } )
            .then( data => {
              if ( ! data || ! data.comments ) return;
              comments.value = data.comments;
            } )
            .finally( () => {
              loading.value = false;
              setLineVariable();

              if ( comments.value.length > 0 && commentLength.value !== comments.value.length ) {
                incidentCommentsRef.value?.scrollIntoView( { behavior: "smooth", block: "start", inline: "nearest" } );
                commentLength.value = comments.value.length;
              }

              if ( 0 === commentLength.value ) {
                elementType.value = 'div';
              }
            } )
            .catch( error => {
              console.error( 'Error fetching comments:', error );
            } );
      } catch ( error ) {
        console.error( 'Error fetching comments:', error );
      }
    };

    /**
     * Update bottom line position based on the last comment height.
     */
    const setLineVariable = () => {
      if ( ! incidentCommentsRef.value ) {
        return;
      }

      const offsetLastChild = incidentCommentsRef.value?.children[ commentLength.value - 1 ]?.offsetHeight || 0;

      if ( 1 > offsetLastChild ) {
        return;
      }

      incidentCommentsRef.value.style.setProperty( '--incident-comment-line-bottom', `${ offsetLastChild }px` );
    }

    onMounted( () => {
      fetchComments();

      // Set line bottom on mount for the existing comments. DO NOT REMOVE!
      setLineVariable();
      intervalId = setInterval( fetchComments, props.updateInterval * 1000 );

      // Add event listeners for resize and orientation change.
      window.addEventListener( 'resize', setLineVariable );
      window.addEventListener( 'orientationchange', setLineVariable );
    } );

    // Clean up event listeners when component is destroyed.
    onUnmounted( () => {
      window.removeEventListener( 'resize', setLineVariable );
      window.removeEventListener( 'orientationchange', setLineVariable );

      clearInterval( intervalId ); // Clear the interval on component unmount.
    } );

    return {
      ariaLabel,
      appClasses,
      sortedComments,
      loading,
      incidentCommentsRef,
      edit,
      elementType
    };
  },
} );
</script>
